<template>
  <div class="carousel-wrapper">
    <Carousel
      v-model="currentIndex"
      ref="carousel"
      :items-to-show="1"
      :class="{ 'carousel-grow-width': props.growWidth }"
    >
      <Slide
        v-if="props.images.length"
        v-for="img in props.images"
        :key="img.id"
      >
        <img
          class="image"
          :src="img.imagePath"
          :alt="$t('list.product_image')"
        />
      </Slide>
      <Slide v-else :items-to-show="1" :wrap-around="false">
        <div class="image-container">
          <img
            class="empty-image"
            src="@/assets/images/foto.png"
            :alt="$t('list.product_image')"
          />
          <span>{{ emptyMessage ?? "Dodaj zdjęcia" }}</span>
        </div>
      </Slide>
      <template #addons>
        <button class="drag handle" v-if="props.draggable">
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.58333 0.75H0.75V2.58333H2.58333V0.75ZM15.4167 4.41667H17.25V9.91667H15.4167V6.25H6.25V15.4167H9.91667V17.25H4.41667V4.41667H15.4167ZM4.41667 0.75H6.25V2.58333H4.41667V0.75ZM2.58333 4.41667H0.75V6.25H2.58333V4.41667ZM0.75 8.08333H2.58333V9.91667H0.75V8.08333ZM2.58333 11.75H0.75V13.5833H2.58333V11.75ZM8.08333 0.75H9.91667V2.58333H8.08333V0.75ZM13.5833 0.75H11.75V2.58333H13.5833V0.75ZM11.75 13.5833V11.75H17.25V13.5833H15.4167V15.4167H13.5833V17.25H11.75V13.5833ZM15.4167 15.4167V17.25H17.25V15.4167H15.4167Z"
              fill="#A8ACAF"
            />
          </svg>
        </button>
        <div class="carousel-nav" v-if="props.images.length">
          <img
            src="@/assets/icons/circle-arrow-left.svg"
            alt=""
            @click="prev"
          />
          <img
            src="@/assets/icons/circle-arrow-right.svg"
            alt=""
            @click="next"
          />
        </div>
        <button type="button" class="edit-icon" v-if="props.editable">
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16 10C17.1046 10 18 9.10457 18 8C18 6.89543 17.1046 6 16 6C14.8954 6 14 6.89543 14 8C14 9.10457 14.8954 10 16 10Z"
              fill="#A8ACAF"
            />
            <path
              d="M16 18C17.1046 18 18 17.1046 18 16C18 14.8954 17.1046 14 16 14C14.8954 14 14 14.8954 14 16C14 17.1046 14.8954 18 16 18Z"
              fill="#A8ACAF"
            />
            <path
              d="M16 26C17.1046 26 18 25.1046 18 24C18 22.8954 17.1046 22 16 22C14.8954 22 14 22.8954 14 24C14 25.1046 14.8954 26 16 26Z"
              fill="#A8ACAF"
            />
          </svg>
          <div class="action-menu">
            <ul>
              <li role="button" @click="onAddImage">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.5 9.64286H9.64286V13.5H8.35714V9.64286H4.5V8.35714H8.35714V4.5H9.64286V8.35714H13.5V9.64286Z"
                    fill="#498388"
                  />
                  <circle
                    cx="9"
                    cy="9"
                    r="8.3"
                    stroke="#498388"
                    stroke-width="1.4"
                  />
                </svg>

                {{ $t("list.image_add") }}
              </li>
              <li role="button" @click="onRemoveImage">
                <img src="@/assets/icons/delete-v2.svg" alt="Usuń listę" />
                {{ $t("list.image_delete") }}
              </li>
            </ul>
          </div>
        </button>
      </template>
    </Carousel>
  </div>
</template>

<script setup>
import "vue3-carousel/dist/carousel.css";
import { Carousel, Slide } from "vue3-carousel";
import { ref } from "vue";

const carousel = ref(null);
const currentIndex = ref(0);
const props = defineProps({
  /** Image Array to display */
  images: Array,
  /** Adds button with class .drag to allow drag using vuedraggable */
  draggable: Boolean,
  /** Adds button with edit icon */
  editable: Boolean,
  /** Add class that will grow carousel horizontally - width: 100$ */
  growWidth: Boolean,
  /** Message to display if images array is empty */
  emptyMessage: String,
});
function prev() {
  return carousel.value.prev();
}
function next() {
  return carousel.value.next();
}
const emit = defineEmits(["onAddImages", "onRemoveImage"]);
function onAddImage(event) {
  emit("onAddImages");
}
function onRemoveImage() {
  const image = props.images[currentIndex.value];
  if (image) {
    emit("onRemoveImage", image);
    currentIndex.value = 0;
  }
}
</script>

<style lang="scss">
.furniture {
  .carousel {
    &__track {
      width: 100%;
      height: 100%;

      &__slide {
        width: 100% !important;
        height: 100%;
      }
    }
  }
}
</style>

<style scoped lang="scss">
@use "@/css/shopping-list.scss";
.carousel-wrapper {
  position: relative;
  height: fit-content;
}
.carousel {
  display: flex;
  width: 130px;
  height: 130px;
  overflow: hidden;
  position: static;
  background: #ffffff;
  border: 1px solid #e4e6e4;
  border-radius: 10px;
  flex: 1 0 130px;

  &-grow-width {
    @media (max-width: 900px) {
      width: 100%;
    }
  }

  &-nav {
    left: 50%;
    transform: translate(-50%, 0);
    display: flex;
    z-index: 10;
    bottom: 0.5rem;
    gap: 1rem;
    position: absolute;
    img {
      cursor: pointer;
    }
  }
  &:hover {
    .drag {
      visibility: visible;
    }
    .edit-icon {
      visibility: visible;
    }
  }
}
.drag {
  position: absolute;
  border: none;
  background-color: transparent;
  visibility: hidden;
  top: 1rem;
  left: 1rem;
  cursor: pointer;
}
.image-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  font-weight: 500;
  font-size: 13px;
  color: #8c8f94;
}
.empty-image {
  height: 60px;
  width: 60px;
  object-fit: contain;
}
.image {
  height: 100%;
  width: 100%;
  object-fit: contain;
}
.edit-icon {
  position: absolute;
  background-color: transparent;
  border: none;
  right: 0;
  top: 0;
  visibility: hidden;
  cursor: pointer;
  &:focus-within {
    .action-menu {
      top: 0;
      right: -20px;
      visibility: visible;
      opacity: 1;
    }
  }
}
</style>
